import React from 'react';
import { TagColored, Text, SparkIcon } from '@spark';

/**
 * Computes the improvement metadata for a value pair.
 *
 * @param {Object} args
 * @param {number} args.comparison - the comparison model value
 * @param {number} args.current    - the current model value
 * @param {('lower-is-better'|'higher-is-better')} [args.direction]
 * @returns {{kind: 'positive'|'negative'|'empty', text: string, factor?: number}}
 */
export function computeImprovement({ comparison, current, direction = 'lower-is-better' }) {
  const c = Number(comparison);
  const n = Number(current);
  if (!Number.isFinite(c) || !Number.isFinite(n) || c === 0) {
    return { kind: 'empty', text: '-' };
  }

  const diff = n - c;
  const pct = (diff / c) * 100;
  // No meaningful change — render as plain "-" in the table (no Tag).
  if (pct === 0) return { kind: 'empty', text: '-' };

  if (direction === 'lower-is-better') {
    if (n < c) {
      const factor = c / n;
      const text =
        factor >= 1.5
          ? `${factor.toFixed(1)}x faster`
          : `${pct.toFixed(1)}% faster`;
      return { kind: 'positive', text, factor };
    }
    return { kind: 'negative', text: `${pct.toFixed(1)}% slower` };
  }

  // higher-is-better (e.g. throughput rps) — percentage only; no "faster"/"slower"
  // suffix (copy reads cleaner for relative throughput change).
  if (n > c) {
    return { kind: 'positive', text: `+${pct.toFixed(1)}%` };
  }
  return { kind: 'negative', text: `${pct.toFixed(1)}%` };
}

/**
 * Signed % change for the Response Length "change" column (no faster/slower).
 * Icon follows the sign of the change; Tag is default unless |pct| >= threshold.
 *
 * @param {Object} args
 * @param {number} args.comparison
 * @param {number} args.current
 * @param {number} [args.alertThresholdPct]  Absolute % at or above → red Tag (default 10).
 * @returns {{kind: 'change-up'|'change-down'|'empty', text: string, highlight: boolean}}
 */
export function computeSignedPercentChange({
  comparison,
  current,
  alertThresholdPct = 10,
}) {
  const c = Number(comparison);
  const n = Number(current);
  if (!Number.isFinite(c) || !Number.isFinite(n) || c === 0) {
    return { kind: 'empty', text: '-', highlight: false };
  }
  const pct = ((n - c) / c) * 100;
  if (pct === 0) {
    return { kind: 'empty', text: '-', highlight: false };
  }
  const highlight = Math.abs(pct) >= alertThresholdPct;
  if (pct > 0) {
    return {
      kind: 'change-up',
      text: `+${pct.toFixed(1)}%`,
      highlight,
    };
  }
  return {
    kind: 'change-down',
    text: `${pct.toFixed(1)}%`,
    highlight,
  };
}

/**
 * Visual pill for an improvement value.
 * `kind` controls color/icon. `text` is the text to display.
 *
 * @param {Object} props
 * @param {'performance'|'signed-percent'} [props.variant]  `signed-percent` = Response Length change column.
 * @param {boolean} [props.highlight]                       Used with `signed-percent`: red Tag when true.
 */
export default function ImprovementPill({
  kind,
  text,
  disabled,
  variant = 'performance',
  highlight = false,
}) {
  if (disabled) {
    return <Text variant="base-regular" color="secondary">-</Text>;
  }

  if (!kind || kind === 'empty' || kind === 'neutral') {
    return <Text variant="base-regular" color="secondary">-</Text>;
  }

  if (variant === 'signed-percent') {
    const isUp = kind === 'change-up';
    return (
      <TagColored
        label={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <SparkIcon slug={isUp ? 'arrow-narrow-up' : 'arrow-narrow-down'} size={14} />
            {text}
          </span>
        }
        color={highlight ? 'red' : 'grey'}
      />
    );
  }

  const isPositive = kind === 'positive';
  return (
    <TagColored
      label={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <SparkIcon slug={isPositive ? 'arrow-narrow-up' : 'arrow-narrow-down'} size={14} />
          {text}
        </span>
      }
      color={isPositive ? 'green' : 'red'}
    />
  );
}
